import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { SupplierInvoiceForm } from "@/components/finance/SupplierInvoiceForm";
import { SupplierInvoiceStatusBadge } from "@/components/finance/SupplierInvoiceStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { hasCapability } from "@shared/finance/capabilities";
import type { FinanceRole } from "@shared/finance/capabilities";
import { SUPPLIER_INVOICE_STATUSES } from "@shared/finance/supplierInvoices";
import type { SupplierInvoiceStatus } from "@shared/finance/supplierInvoices";
import { ChevronLeft, ChevronRight, Copy, Eye, FileText, Paperclip, Pencil, Plus, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";

const STATUS_LABELS: Record<SupplierInvoiceStatus, string> = {
  draft: "Borrador",
  received: "Recibida",
  pending_review: "Pendiente revisión",
  approved: "Aprobada",
  scheduled_for_payment: "Programada para pago",
  partially_paid: "Parcialmente pagada",
  paid: "Pagada",
  cancelled: "Cancelada",
};

function formatEUR(amount: string | number | null | undefined) {
  if (amount === null || amount === undefined) return "-";
  const value = typeof amount === "string" ? parseFloat(amount) : amount;
  return Number.isFinite(value)
    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(value)
    : "-";
}

function formatDate(value: string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("es-ES");
}

function asNumber(amount: unknown) {
  const value = typeof amount === "string" ? parseFloat(amount) : Number(amount);
  return Number.isFinite(value) ? value : 0;
}

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number] | "max";
const STATUS_ALL = "__all__" as const;
type StatusFilter = SupplierInvoiceStatus | typeof STATUS_ALL;

type SupplierInvoicesContentProps = { embedded?: boolean };

export function SupplierInvoicesContent({ embedded = false }: SupplierInvoicesContentProps) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const canRead = user ? hasCapability(user.role as FinanceRole, "finance.read") : false;
  const canWrite = user ? hasCapability(user.role as FinanceRole, "finance.write") : false;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(STATUS_ALL);
  const [search, setSearch] = useState("");
  const [paymentMonth, setPaymentMonth] = useState("");
  const [paymentFrom, setPaymentFrom] = useState("");
  const [paymentTo, setPaymentTo] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [duplicateSourceInvoiceId, setDuplicateSourceInvoiceId] = useState<number | null>(null);
  const isMaxPageSize = pageSize === "max";
  const invalidPaymentRange = Boolean(paymentFrom && paymentTo && paymentFrom > paymentTo);

  const query = trpc.supplierInvoices.list.useQuery(
    {
      status: statusFilter === STATUS_ALL ? undefined : statusFilter,
      search: search.trim() || undefined,
      paymentMonth: paymentMonth || undefined,
      paymentFrom: paymentFrom || undefined,
      paymentTo: paymentTo || undefined,
      limit: pageSize === "max" ? 200 : pageSize,
      offset: page * (pageSize === "max" ? 200 : pageSize),
    },
    { enabled: canRead && !invalidPaymentRange },
  );

  const deleteMutation = trpc.supplierInvoices.softDelete.useMutation({
    onSuccess: () => {
      toast.success("Gasto eliminado");
      query.refetch();
    },
    onError: (error) => toast.error("No se pudo eliminar el gasto: " + error.message),
  });

  const invoices = invalidPaymentRange ? [] : ((query.data as Array<Record<string, unknown>>) ?? []);

  const pageTotals = useMemo<{ taxableBase: number; vatAmount: number; totalAmount: number }>(
    () => invoices.reduce<{ taxableBase: number; vatAmount: number; totalAmount: number }>(
      (totals, invoice) => ({
        taxableBase: totals.taxableBase + asNumber(invoice.taxableBase),
        vatAmount: totals.vatAmount + asNumber(invoice.vatAmount),
        totalAmount: totals.totalAmount + asNumber(invoice.totalAmount),
      }),
      { taxableBase: 0, vatAmount: 0, totalAmount: 0 },
    ),
    [invoices],
  );

  function clearFilters() {
    setStatusFilter(STATUS_ALL);
    setSearch("");
    setPaymentMonth("");
    setPaymentFrom("");
    setPaymentTo("");
    setPage(0);
  }

  function openCreateForm() {
    setEditingInvoiceId(null);
    setDuplicateSourceInvoiceId(null);
    setFormOpen(true);
  }

  function openEditForm(invoiceId: number) {
    setDuplicateSourceInvoiceId(null);
    setEditingInvoiceId(invoiceId);
    setFormOpen(true);
  }

  function openDuplicateForm(invoiceId: number) {
    setEditingInvoiceId(null);
    setDuplicateSourceInvoiceId(invoiceId);
    setFormOpen(true);
  }

  function obraLabel(invoice: Record<string, unknown>) {
    if (invoice.allocationTarget === "warehouse") return "Almacén";
    if (invoice.allocationTarget === "general_overhead") return "Gastos generales";
    if (invoice.allocationTarget === "unallocated") return "Pendiente de imputar";
    if (invoice.allocationTarget === "installation") {
      const code = (invoice.installationCode as string | null)?.trim();
      return code ? `Exp. ${code}` : "Expediente sin código";
    }
    return "Sin asignar";
  }

  function installationContextLabel(invoice: Record<string, unknown>) {
    if (invoice.allocationTarget !== "installation") return null;
    const client = typeof invoice.installationClientName === "string" ? invoice.installationClientName.trim() : "";
    const site = typeof invoice.installationSiteName === "string" ? invoice.installationSiteName.trim() : "";
    const work = typeof invoice.installationWorkDescription === "string" ? invoice.installationWorkDescription.trim() : "";
    const context = [client, site, work].filter(Boolean).join(" · ");
    return context || "Contexto de instalación no disponible";
  }

  function handleDelete(invoice: Record<string, unknown>) {
    if (invoice.status === "paid") {
      toast.error("No se puede eliminar una factura pagada");
      return;
    }
    if (window.confirm("¿Eliminar este gasto? Quedará archivado y no aparecerá en la vista normal.")) {
      deleteMutation.mutate({ id: Number(invoice.id), reason: "Eliminado desde Gastos" });
    }
  }

  if (user && !canRead) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-lg font-medium">Acceso denegado</p>
        <p className="text-sm text-muted-foreground">No tienes permisos para ver esta sección.</p>
        <Button variant="outline" onClick={() => setLocation("/")}>Volver al inicio</Button>
      </div>
    );
  }

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
      <FileText className="h-5 w-5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">No hay facturas recibidas en esta vista.</p>
      {canWrite && <Button onClick={() => setFormOpen(true)}><Plus className="mr-1.5 h-4 w-4" />Registrar primera factura recibida</Button>}
    </div>
  );

  return (
    <div className="space-y-4">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-semibold">Gastos</h1>
          <p className="text-sm text-muted-foreground">Registro, pago y clasificación de las facturas recibidas.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/20 p-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Input placeholder="Buscar factura, obra o proveedor..." value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} className="h-9 w-64" />
          <Input type="month" aria-label="Mes de pago" value={paymentMonth} onChange={(event) => { setPaymentMonth(event.target.value); setPage(0); }} className="h-9 w-44" />
          <Input type="date" aria-label="Pago desde" value={paymentFrom} onChange={(event) => { setPaymentFrom(event.target.value); setPage(0); }} className="h-9 w-36" />
          <Input type="date" aria-label="Pago hasta" value={paymentTo} onChange={(event) => { setPaymentTo(event.target.value); setPage(0); }} className="h-9 w-36" />
          <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value as StatusFilter); setPage(0); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Todos los estados" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={STATUS_ALL}>Todos los estados</SelectItem>
              {SUPPLIER_INVOICE_STATUSES.map((status) => <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>)}
            </SelectContent>
          </Select>
          {(search || paymentMonth || paymentFrom || paymentTo || statusFilter !== STATUS_ALL) && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="mr-1 h-4 w-4" />Borrar filtros</Button>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Filas</span>
          <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(value === "max" ? "max" : Number(value) as PageSize); setPage(0); }}>
            <SelectTrigger className="h-9 w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}
              <SelectItem value="max">Máx. 200</SelectItem>
            </SelectContent>
          </Select>
          {canWrite && <Button onClick={openCreateForm}><Plus className="mr-1.5 h-4 w-4" />Nueva factura recibida</Button>}
        </div>
        {invalidPaymentRange && <p className="basis-full text-sm text-destructive">La fecha de pago desde no puede ser posterior a la fecha hasta.</p>}
      </div>

      {!query.isLoading && !query.isError && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Base imponible visible</p><p className="text-lg font-semibold">{formatEUR(pageTotals.taxableBase)}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">IVA visible</p><p className="text-lg font-semibold">{formatEUR(pageTotals.vatAmount)}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Total visible</p><p className="text-lg font-semibold">{formatEUR(pageTotals.totalAmount)}</p></CardContent></Card>
        </div>
      )}

      {query.isLoading && <div className="py-12 text-center text-muted-foreground">Cargando gastos...</div>}
      {query.isError && <div className="py-12 text-center text-destructive">Error al cargar gastos: {query.error.message}</div>}

      {!query.isLoading && !query.isError && (
        <>
          <div className="hidden overflow-x-auto rounded-lg border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha factura</TableHead>
                  <TableHead>Fecha alta</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead>Factura</TableHead>
                  <TableHead>Proveedor / concepto</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="text-right">Base imponible</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Fecha de pago</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={11}>{emptyState}</TableCell></TableRow>
                ) : invoices.map((invoice) => {
                  const documentId = Number(invoice.documentId);
                  const hasDocument = Boolean(invoice.hasDocument && Number.isFinite(documentId));
                  const apiUrl = (import.meta as { env: Record<string, string> }).env.VITE_API_URL || "";
                  return (
                    <TableRow key={String(invoice.id)}>
                      <TableCell>{formatDate(invoice.invoiceDate as string | null)}</TableCell>
                      <TableCell>{formatDate(invoice.createdAt as string | null)}</TableCell>
                      <TableCell>
                        <div><Badge variant="outline">{obraLabel(invoice)}</Badge></div>
                        {installationContextLabel(invoice) && (
                          <div className="mt-1 max-w-64 truncate text-xs text-muted-foreground" title={installationContextLabel(invoice) ?? undefined}>
                            {installationContextLabel(invoice)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {hasDocument ? (
                          <a href={apiUrl + "/api/finance/supplier-invoices/documents/" + documentId + "/view"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-ods-navigation hover:underline">
                            <Paperclip className="h-4 w-4" />{String(invoice.invoiceNumber || "Sin número")}
                          </a>
                        ) : String(invoice.invoiceNumber || "Sin número")}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{String(invoice.supplierName || "Proveedor #" + invoice.supplierId)}</div>
                        <div className="max-w-48 truncate text-xs text-muted-foreground">{String(invoice.concept || "Sin concepto")}</div>
                      </TableCell>
                      <TableCell>
                        <div>{String(invoice.categoryName || invoice.categoryCode || "Automática")}</div>
                        <div className="text-xs text-muted-foreground">{invoice.categoryCode ? "Manual" : "Automática"}</div>
                      </TableCell>
                      <TableCell className="text-right">{formatEUR(invoice.taxableBase as string)}</TableCell>
                      <TableCell className="text-right font-medium">{formatEUR(invoice.totalAmount as string)}</TableCell>
                      <TableCell>{formatDate(invoice.paymentDate as string | null)}</TableCell>
                      <TableCell><SupplierInvoiceStatusBadge status={String(invoice.status)} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canWrite && invoice.status === "draft" && <Button size="icon" variant="ghost" title="Editar gasto" aria-label="Editar gasto" onClick={() => openEditForm(Number(invoice.id))}><Pencil className="h-4 w-4" /></Button>}
                          {canWrite && <Button size="icon" variant="ghost" title="Duplicar factura" aria-label="Duplicar factura" onClick={() => openDuplicateForm(Number(invoice.id))}><Copy className="h-4 w-4" /></Button>}
                          <Button size="icon" variant="ghost" title="Ver gasto" aria-label="Ver gasto" onClick={() => setLocation("/accounting/supplier-invoices/" + invoice.id)}><Eye className="h-4 w-4" /></Button>
                          {canWrite && <Button size="icon" variant="ghost" title="Eliminar gasto" aria-label="Eliminar gasto" onClick={() => handleDelete(invoice)} disabled={deleteMutation.isPending}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {invoices.length === 0 ? emptyState : invoices.map((invoice) => (
              <Card key={String(invoice.id)}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{String(invoice.supplierName || "Proveedor #" + invoice.supplierId)}</span>
                    <SupplierInvoiceStatusBadge status={String(invoice.status)} />
                  </div>
                  <div className="text-sm text-muted-foreground">{String(invoice.concept || "Sin concepto")}</div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">{obraLabel(invoice)}</div>
                      {installationContextLabel(invoice) && (
                        <div className="truncate text-xs text-muted-foreground" title={installationContextLabel(invoice) ?? undefined}>
                          {installationContextLabel(invoice)}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 font-semibold">{formatEUR(invoice.totalAmount as string)}</span>
                  </div>
                  <div className="flex gap-2">
                    <>{canWrite && invoice.status === "draft" && <Button size="sm" variant="outline" onClick={() => openEditForm(Number(invoice.id))}><Pencil className="mr-1 h-4 w-4" />Editar</Button>}<Button size="sm" variant="ghost" onClick={() => setLocation("/accounting/supplier-invoices/" + invoice.id)}><Eye className="mr-1 h-4 w-4" />Ver</Button></>
                    {canWrite && <Button size="sm" variant="ghost" aria-label="Duplicar factura" onClick={() => openDuplicateForm(Number(invoice.id))}><Copy className="mr-1 h-4 w-4" />Duplicar</Button>}
                    {canWrite && <Button size="sm" variant="ghost" onClick={() => handleDelete(invoice)}><Trash2 className="mr-1 h-4 w-4 text-destructive" />Eliminar</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{invoices.length > 0 ? (page * (pageSize === "max" ? 200 : pageSize) + 1) + "–" + (page * (pageSize === "max" ? 200 : pageSize) + invoices.length) + " resultados" : "0 resultados"}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={page === 0}><ChevronLeft className="h-4 w-4" />Anterior</Button>
              <Button size="sm" variant="outline" onClick={() => setPage((value) => value + 1)} disabled={isMaxPageSize || (query.data?.length ?? 0) < (isMaxPageSize ? 200 : pageSize)}>Siguiente<ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}

      <SupplierInvoiceForm invoiceId={editingInvoiceId ?? undefined} duplicateFromInvoiceId={duplicateSourceInvoiceId ?? undefined} open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) { setEditingInvoiceId(null); setDuplicateSourceInvoiceId(null); } }} onSuccess={() => query.refetch()} />
    </div>
  );
}

export default function SupplierInvoices() {
  return <DashboardLayout><SupplierInvoicesContent /></DashboardLayout>;
}
